import * as THREE from 'three';
import getProjectionMatrix from '../utils/getProjectionMatrix';
import { getCamera, getCameraWorldTransform, getRect, getThreeCamera } from '../../modules/player';
import { find, get, getWorldTransform, set, filter } from '../../modules/scene';
import { getSelectedNodes } from '../../modules/selection';
import { drawMoveGizmo, highlightAxis, getViewScale } from './gizmos';
import getProjectedPoint from './utils/getProjectedPoint';
const { PI, sin } = Math;

const invCameraWorldMatrix = new THREE.Matrix4();
const cameraProjectMatrix = new THREE.Matrix4();
const cameraUnprojectMatrix = new THREE.Matrix4();
const inverseParent = new THREE.Matrix4();
const referencePoint = new THREE.Vector3();
const deltaReferencePoint = new THREE.Vector3();

//const defaultOptions = {
//  displayGizmo: true,
//  useXAxis: true,
//  useYAxis: true,
//  useZAxis: true,
//  defaultPlane: { x: 0, y: 0 },
//}

//updateTool('nodeMove', { displayGizmo: false });

export default function nodeMove(store, translator) {
  let activeAxis = false;

  return {
    label: 'Move Node',
    enabled: true,
    display: {
      playerTools: false,
    },

    options: {
      displayGizmo: true,
      mode: 'free',
      plane: {
        normal: { x: 0, y: 1, z: 0 },
        constant: 0,
      },
    },

    tool: {
      hover: function (ev) {
        if (!this.active) return;
        activeAxis = highlightAxis(translator, ev, activeAxis);
      },

      drag: function (ev) {
        if (ev.which !== 1 || !this.active || ev.metaKey) return false;
        const self = this;
        return {
          momentum: !activeAxis,
          handle: function (ev) {
            switch (self.options.mode) {
              case 'plane':
                moveNodeOnPlane(store, ev.deltaX, ev.deltaY, self.options.plane);
                break;
              case 'objects':
                moveNodeOnObjects(store, ev.clientX, ev.clientY, self.options.objects);
                break;
              default:
                moveNodeFree(store, ev.deltaX, ev.deltaY, activeAxis);
                break;
            }
          },
        };
      },
    },

    widget: {
      draw: function (manipulatorsObject) {
        manipulatorsObject.add(drawMoveGizmo());
        const scale = getViewScale(store);
        manipulatorsObject.scale.set(scale, scale, scale);
      },

      position: function (manipulatorsObject) {
        const selectedNodes = getSelectedNodes(store);
        if (!selectedNodes.length) {
          manipulatorsObject.visible = false;
          return;
        }

        manipulatorsObject.visible = true;
        const manipId = selectedNodes[0];
        const worldTransform = getWorldTransform(store, manipId);
        const position = new THREE.Vector3();
        position.setFromMatrixPosition(worldTransform);
        manipulatorsObject.position.set(position.x, position.y, position.z);
        //manipulatorsObject.scale.set(2,2,2);
        const scale = getViewScale(store);
        manipulatorsObject.scale.set(scale, scale, scale);
      },
    },
  };
}

function moveNodeFree(store, x, y, restrictToAxis) {
  const selectedNodes = getSelectedNodes(store);
  if (!selectedNodes.length) return;
  const manipId = selectedNodes[0];

  const cameraWorldMatrix = getCameraWorldTransform(store);
  const nodeMatrix = getWorldTransform(store, manipId);
  if (!nodeMatrix) return;

  const parentMatrix = getWorldTransform(store, find(store, { id: manipId, parent: true }));
  const nodePosition = new THREE.Vector3().setFromMatrixPosition(nodeMatrix);
  deltaReferencePoint.copy(getProjectedPoint(store, manipId, x, y));

  if (restrictToAxis) {
    const projVector = new THREE.Vector3(0, 0, 0);
    projVector[restrictToAxis] = 1;

    const targToCam = new THREE.Vector3().setFromMatrixPosition(cameraWorldMatrix).sub(nodePosition);
    const origin = new THREE.Vector3(0, 0, 0);
    const plane = new THREE.Plane().setFromCoplanarPoints(targToCam, projVector, origin);
    deltaReferencePoint.projectOnPlane(plane.normal);

    const camToMove = new THREE.Vector3().subVectors(deltaReferencePoint, targToCam);
    deltaReferencePoint.projectOnVector(projVector).normalize();
    const alpha = targToCam.angleTo(deltaReferencePoint);
    const beta = PI - targToCam.angleTo(camToMove);
    const gamma = PI - alpha - beta;
    const axis = targToCam.length() * sin(beta) / sin(gamma);
    deltaReferencePoint.multiplyScalar(axis);
  }

  nodePosition.add(deltaReferencePoint);

  nodeMatrix.setPosition(nodePosition);
  if (parentMatrix) nodeMatrix.premultiply(inverseParent.getInverse(parentMatrix));

  nodePosition.setFromMatrixPosition(nodeMatrix);

  // FIXME: Restrict to axis:
  //if (restrictToAxis) console.log('restrict to axis: ', restrictToAxis);

  store.dispatch(set({ id: manipId, plug: 'Transform', property: 'translation' }, nodePosition));
}

const upVector = new THREE.Vector3(0, 1, 0);

const stripParentTransform = function () {
  const newLocalTransform = new THREE.Matrix4();
  const invParentTransform = new THREE.Matrix4();

  return (store, nodeId, position, quaternion, scale) => {
    newLocalTransform.compose(position, quaternion, scale);

    const parentId = find(store, { id: nodeId, parent: true });
    const parentTransform = getWorldTransform(store, parentId);
    if (parentTransform) {
      invParentTransform.getInverse(parentTransform, true);
      newLocalTransform.premultiply(invParentTransform);
      newLocalTransform.decompose(position, quaternion, scale);
    }
  };
}();

const setNodePosition = function () {
  const scale = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  const euler = new THREE.Euler();

  return (store, nodeId, position) => {
    const scaleNode = get(store, { id: nodeId, plug: 'Transform', property: 'scale' });
    const rotateNode = get(store, { id: nodeId, plug: 'Transform', property: 'rotation' });
    const rotateOrder = get(store, { id: nodeId, plug: 'Transform', property: 'rotateOrder' });
    scale.set(scaleNode.x, scaleNode.y, scaleNode.z);
    euler.set(rotateNode, rotateOrder);
    quaternion.setFromEuler(euler);

    stripParentTransform(store, nodeId, position, quaternion, scale);


    //store.dispatch(set({id:nodeId, plug:'Transform', property:'rotation'}, newEuler.toVector3().multiplyScalar(180/Math.PI)));
    store.dispatch(set({ id: nodeId, plug: 'Transform', property: 'translation' }, position));
  };
}();

const getNodeNdc = function () {
  const oldPosition = new THREE.Vector3();
  const inverseCameraMatrix = new THREE.Matrix4();

  return (store, nodeId) => {
    oldPosition.setFromMatrixPosition(getWorldTransform(store, nodeId));
    const camera = getThreeCamera(store);
    inverseCameraMatrix.getInverse(camera.matrix, true).premultiply(camera.projectionMatrix);
    oldPosition.applyMatrix4(inverseCameraMatrix);
    return oldPosition;
  };
}();

const moveNodeOnObjects = function () {
  const newQuaternion = new THREE.Quaternion();

  return (store, x, y, objects) => {
    const rect = getRect(store);
    const nodeId = getSelectedNodes(store)[0];
    if (!nodeId) return;

    const ndc = { x: 2 * x / rect.width - 1, y: -2 * y / rect.height + 1 };
    const hits = store.getTranslator().raycastSelect(ndc);
    const nodeChildren = filter(store, { from: { id: nodeId } });
    const nodeHit = hits.find((hit)=> {
      const foundIndex = objects.findIndex((id)=> {
        return id === hit.id && id !== nodeId && nodeChildren.indexOf(id) < 0;
      });
      return foundIndex >= 0;
    });

    if (!nodeHit) return;

    let newPosition = nodeHit.data[0].point;

    setNodePosition(store, nodeId, newPosition);
  };
}();

const moveNodeOnPlane = function () {
  const newPosition = new THREE.Vector3();
  const newQuaternion = new THREE.Quaternion();
  const moveOnPlane = new THREE.Plane();
  const moveOnNormal = new THREE.Vector3();

  return (store, x, y, plane) => {
    const rect = getRect(store);
    const nodeId = getSelectedNodes(store)[0];
    if (!nodeId) return;
    const oldNdc = getNodeNdc(store, nodeId);

    //const plane = store.getIn(['selection', 'collisionPlane']);
    const normal = plane.normal;

    moveOnNormal.set(normal.x, normal.y, normal.z);
    moveOnPlane.set(moveOnNormal, plane.constant);

    const ndc = { x: oldNdc.x + 2 * x / rect.width, y: oldNdc.y - 2 * y / rect.height };
    const ray = store.getTranslator().getCameraMouseRay(ndc).ray;
    if (!ray.intersectsPlane(moveOnPlane)) return;
    ray.intersectPlane(moveOnPlane, newPosition);

    store.dispatch(set({ id: nodeId }));
    setNodePosition(store, nodeId, newPosition);
  };
}();



// WEBPACK FOOTER //
// ./src/translators/tools/nodeMove.js